/**
 * Freedom Academy — public learning page (categories, courses, course detail, pass purchase)
 * Subscription-driven via AT wallet. 18 categories. en/hi/mr i18n.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GraduationCap, BookOpen, Lock, Sparkles, ChevronRight, Trophy, Globe,
  Crown, CheckCircle2, Play, Clock, ChevronLeft, Award,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
];

const TIER_THEME = {
  monthly:   { color: 'from-emerald-500 to-teal-500', badge: 'Starter' },
  quarterly: { color: 'from-sky-500 to-indigo-500',    badge: 'Popular' },
  half_year: { color: 'from-amber-500 to-orange-500',  badge: 'Saver' },
  yearly:    { color: 'from-pink-500 to-rose-500',     badge: 'Best Value' },
};

// ============ Categories + Course Listing ============
export const Academy = () => {
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('academy_lang') || 'en');
  const [activePass, setActivePass] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ students: [], promoters: [], quiz_masters: [] });
  const [badgeInfo, setBadgeInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/academy/categories').then((r) => setCategories(r.data.categories));
    api.get('/academy/tiers').then((r) => setTiers(r.data.tiers));
    api.get('/academy/passes/me').then((r) => setActivePass(r.data.has_active_pass)).catch(() => {});
    api.get('/academy/leaderboard/students').then((r) => setLeaderboard((s) => ({ ...s, students: r.data.students }))).catch(() => {});
    api.get('/academy/leaderboard/promoters').then((r) => setLeaderboard((s) => ({ ...s, promoters: r.data.promoters }))).catch(() => {});
    api.get('/academy/leaderboard/quiz-masters').then((r) => setLeaderboard((s) => ({ ...s, quiz_masters: r.data.quiz_masters }))).catch(() => {});
    api.get('/academy/badges/me').then((r) => setBadgeInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = selectedCat ? `?category_id=${selectedCat}&language=${lang}` : `?language=${lang}`;
    api.get(`/academy/courses${q}`).then((r) => setCourses(r.data.courses)).catch(() => {});
  }, [selectedCat, lang]);

  useEffect(() => { localStorage.setItem('academy_lang', lang); }, [lang]);

  const purchase = async (tierId) => {
    setPurchasing(tierId);
    try {
      const r = await api.post('/academy/passes/purchase', { tier_id: tierId });
      toast.success(`🎓 Pass activated! Expires ${new Date(r.data.pass.expires_at).toLocaleDateString()}`);
      setActivePass(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally { setPurchasing(null); }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="academy-home">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-amber-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"><GraduationCap className="w-4 h-4" /> Anant · Freedom Academy</div>
            <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-amber-300 via-pink-300 to-violet-300 bg-clip-text text-transparent mt-1">
              Learn · Earn · Rise
            </h1>
            <p className="text-slate-400 text-sm mt-1">18 categories · Nursery to Class 12 · NEET · JEE · Personal Dev · Meditation · Social Media.</p>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-amber-300" />
            <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="academy-lang-select" className="bg-white/10 border border-white/15 rounded-lg px-3 py-1.5 text-sm">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>

        {/* Pass banner */}
        {!activePass ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="academy-tiers">
            {tiers.map((t) => {
              const theme = TIER_THEME[t.id] || { color: 'from-slate-500 to-slate-700', badge: '' };
              return (
                <div key={t.id} data-testid={`academy-tier-${t.id}`} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 relative overflow-hidden">
                  <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${theme.color} opacity-20 blur-2xl`} />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.name} Pass</span>
                      {theme.badge && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${theme.color} text-white`}>{theme.badge}</span>}
                    </div>
                    <div className="text-2xl font-extrabold text-white">{t.price_at.toLocaleString('en-IN')} <span className="text-xs text-slate-400 font-normal">AT</span></div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Valid for {t.duration_days} days · All courses unlocked</div>
                    <button
                      onClick={() => purchase(t.id)}
                      disabled={purchasing === t.id}
                      data-testid={`academy-buy-${t.id}`}
                      className={`mt-3 w-full py-2 rounded-lg bg-gradient-to-r ${theme.color} text-white font-bold text-sm shadow disabled:opacity-60 flex items-center justify-center gap-1.5`}
                    >
                      {purchasing === t.id ? 'Activating…' : <>Buy Pass <ChevronRight className="w-3 h-3" /></>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 mb-6 flex items-center justify-between gap-3" data-testid="academy-active-banner">
            <div className="flex items-center gap-2 text-emerald-200 font-bold text-sm"><Crown className="w-4 h-4" /> Digital Access Pass active — all courses unlocked.</div>
            <Link to="/academy/passes" className="text-xs underline text-emerald-200">View pass</Link>
          </div>
        )}

        {/* Categories */}
        <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Categories</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedCat(null)} data-testid="academy-cat-all" className={`px-3 py-1.5 rounded-full text-xs font-bold border ${selectedCat === null ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}>All</button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCat(c.id)}
                data-testid={`academy-cat-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${selectedCat === c.id ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}
              >
                {c.label[lang] || c.label.en}
              </button>
            ))}
          </div>
        </div>

        {/* Courses grid */}
        {courses.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="academy-empty">
            <Sparkles className="w-10 h-10 text-amber-300 mx-auto mb-3" />
            <div className="text-lg font-bold">No courses here yet</div>
            <div className="text-slate-400 text-sm mt-1 mb-4">Admin can add courses, or active pass holders can generate AI courses below.</div>
            {activePass && selectedCat && (
              <AIGenerator categoryId={selectedCat} language={lang} onGenerated={(c) => navigate(`/academy/course/ai/${c.id}`)} />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="academy-courses-grid">
            {courses.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/academy/course/${c.id}`)}
                data-testid={`academy-course-${c.id}`}
                className="text-left rounded-2xl border border-white/10 bg-slate-950/60 overflow-hidden hover:border-amber-500/40 transition group"
              >
                <div className="h-32 w-full bg-gradient-to-br from-amber-500/20 to-pink-500/20 flex items-center justify-center relative overflow-hidden">
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                  ) : (
                    <BookOpen className="w-10 h-10 text-amber-300/60" />
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">{categories.find((x) => x.id === c.category_id)?.label?.[lang] || ''}</div>
                  <div className="font-extrabold text-white text-sm mt-0.5 line-clamp-2">{c.title}</div>
                  {c.desc && <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{c.desc}</div>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* AI Generator — also shown above leaderboards when there ARE admin courses, but pass-holder wants more */}
        {activePass && selectedCat && courses.length > 0 && (
          <div className="mb-6">
            <AIGenerator categoryId={selectedCat} language={lang} onGenerated={(c) => navigate(`/academy/course/ai/${c.id}`)} />
          </div>
        )}

        {/* My Achievements (Badges) */}
        {badgeInfo && <MyBadgesCard info={badgeInfo} />}

        {/* Leaderboards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <LeaderCard title="Top Students (30d)" icon={Award} items={leaderboard.students} valueKey="total_reward" suffix="AT" testid="academy-lb-students" />
          <LeaderCard title="Quiz Masters (30d)" icon={Trophy} items={leaderboard.quiz_masters} valueKey="quizzes_passed" suffix="passed" testid="academy-lb-quiz-masters" />
          <LeaderCard title="Top Promoters (30d)" icon={Trophy} items={leaderboard.promoters} valueKey="total_earned" suffix="AT" testid="academy-lb-promoters" />
        </div>
      </div>
    </div>
  );
};

const LeaderCard = ({ title, icon: Icon, items, valueKey, suffix, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-amber-300" /><span className="text-sm font-bold">{title}</span></div>
    {(!items || items.length === 0) ? (
      <div className="text-xs text-slate-500">No data yet.</div>
    ) : (
      <div className="space-y-1.5">
        {items.slice(0, 10).map((r, i) => (
          <div key={r.user_id || i} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1.5">
            <span className="flex items-center gap-2"><span className="text-amber-300 font-bold w-4">{i + 1}.</span> <span className="text-white truncate">{r.user}</span></span>
            <span className="text-emerald-300 font-bold">{Math.round(r[valueKey] || 0)} {suffix}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TIER_BORDER = {
  bronze:   'border-orange-400/50 bg-orange-500/10',
  silver:   'border-slate-300/40 bg-slate-400/10',
  gold:     'border-amber-400/60 bg-amber-500/10',
  platinum: 'border-cyan-300/60 bg-cyan-400/10',
};

const MyBadgesCard = ({ info }) => {
  const { badges = [], stats = {}, unlocked_count = 0, total_count = 0 } = info || {};
  const pct = total_count ? Math.round((unlocked_count / total_count) * 100) : 0;

  const shareBadge = async (badgeId) => {
    try {
      const r = await api.get(`/academy/badges/${badgeId}/share-payload`);
      window.open(r.data.whatsapp, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Share unavailable');
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-900/70 via-indigo-950/60 to-slate-900/70 p-4 mb-4" data-testid="academy-my-badges">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-300" />
          <span className="text-sm font-bold">My Achievements</span>
          <span className="text-[11px] text-slate-400">· {unlocked_count}/{total_count} unlocked</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-300">
          <span>🎓 {stats.modules_completed || 0} modules</span>
          <span>🎯 {stats.quizzes_passed || 0} quizzes</span>
          <span>📜 {stats.certificates || 0} certs</span>
        </div>
      </div>
      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-gradient-to-r from-amber-400 via-pink-400 to-violet-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {badges.map((b) => (
          <div
            key={b.id}
            data-testid={`badge-${b.id}`}
            title={b.desc}
            className={`relative group rounded-xl border p-2 text-center transition ${b.unlocked ? TIER_BORDER[b.tier] || 'border-amber-400/50 bg-amber-500/10' : 'border-white/5 bg-white/[0.02] opacity-40 grayscale'}`}
          >
            <div className="text-2xl leading-none mb-1">{b.icon}</div>
            <div className="text-[11px] font-bold text-white truncate">{b.title}</div>
            <div className="text-[9px] text-slate-400 mt-0.5 line-clamp-2">{b.desc}</div>
            {b.unlocked ? (
              <button
                onClick={() => shareBadge(b.id)}
                data-testid={`share-badge-${b.id}`}
                className="mt-1.5 w-full text-[9px] font-bold uppercase tracking-wider py-1 rounded-md bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-200 border border-emerald-400/30 transition"
                title="Share this badge on WhatsApp"
              >
                📤 Share
              </button>
            ) : (
              <div className="text-[9px] text-slate-500 mt-1 uppercase tracking-wider">Locked</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ AI Course Generator (active pass holders only) ============
const AIGenerator = ({ categoryId, language, onGenerated }) => {
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post('/academy/ai-generate-course', { category_id: categoryId, topic_hint: topic || null, language });
      toast.success('✨ AI Course ready!');
      onGenerated && onGenerated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI generation failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="academy-ai-generator">
      <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-1 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> AI Course Generator (Pro)</div>
      <div className="text-sm font-bold mb-2">Generate a 5-module mini-course in {language === 'hi' ? 'Hindi' : language === 'mr' ? 'Marathi' : 'English'}</div>
      <div className="flex gap-2 flex-wrap">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Optional: specific topic (e.g. 'Photosynthesis for Class 7')" className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="ai-gen-topic" />
        <button onClick={go} disabled={busy} data-testid="ai-gen-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm disabled:opacity-60">
          {busy ? 'Generating…' : '✨ Generate'}
        </button>
      </div>
    </div>
  );
};

// ============ Course Detail ============
export const AcademyCourse = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(() => localStorage.getItem('academy_lang') || 'en');
  const [activeModule, setActiveModule] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/academy/courses/${id}?language=${lang}`)
      .then((r) => { setCourse(r.data); setActiveModule(r.data.modules?.[0] || null); })
      .catch(() => toast.error('Course not found'))
      .finally(() => setLoading(false));
  }, [id, lang]);

  const completeModule = async (mid) => {
    try {
      const r = await api.post(`/academy/modules/${mid}/complete`);
      if (r.data.already_rewarded) {
        toast.info('Already completed — no double reward.');
      } else {
        toast.success(`🎉 +${r.data.reward_at} AT earned!`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen pt-24 text-center text-slate-400">Loading…</div>;
  if (!course) return <div className="min-h-screen pt-24 text-center text-slate-400">Course not found</div>;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="academy-course-detail">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/academy')} className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Academy
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white">{course.title}</h1>
            {course.desc && <p className="text-slate-300/80 text-sm mt-1">{course.desc}</p>}
          </div>
          <CertificateButton courseId={id} />
        </div>
        {!course.has_pass && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 mb-4" data-testid="academy-paywall-notice">
            <Lock className="w-3.5 h-3.5 inline mr-1" /> Only the demo module is free. Purchase a Digital Access Pass to unlock all modules.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 rounded-2xl border border-white/10 bg-slate-950/60 p-3 max-h-[60vh] overflow-y-auto">
            {(course.modules || []).map((m, i) => (
              <button
                key={m.id}
                onClick={() => setActiveModule(m)}
                data-testid={`academy-module-tile-${i}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg mb-1.5 flex items-center gap-2 ${activeModule?.id === m.id ? 'bg-amber-500/15 border border-amber-500/40' : 'hover:bg-white/5'}`}
              >
                <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate">{m.title}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    {m.is_demo ? <><Play className="w-2.5 h-2.5" /> Free demo</> : m.locked ? <><Lock className="w-2.5 h-2.5" /> Locked</> : <><CheckCircle2 className="w-2.5 h-2.5" /> Unlocked</>}
                  </div>
                </div>
                {m.points_reward > 0 && <span className="text-[9px] text-emerald-300 font-bold">+{m.points_reward} AT</span>}
              </button>
            ))}
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-slate-950/60 p-5">
            {!activeModule ? (
              <div className="text-slate-400 text-sm">Select a module to begin</div>
            ) : activeModule.locked ? (
              <div className="text-center py-10">
                <Lock className="w-10 h-10 text-amber-300 mx-auto mb-3" />
                <div className="text-lg font-bold">This module is locked</div>
                <div className="text-slate-400 text-sm mt-1">Purchase a Digital Access Pass to unlock the full course.</div>
                <button onClick={() => navigate('/academy')} className="mt-4 px-5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold" data-testid="academy-buy-pass-cta">Buy Pass</button>
              </div>
            ) : (
              <div data-testid="academy-module-viewer">
                <div className="text-xs font-bold uppercase tracking-widest text-amber-300">Module · {activeModule.is_demo ? 'Free Demo' : 'Premium'}</div>
                <h2 className="text-xl font-extrabold mt-1 mb-3">{activeModule.title}</h2>
                {/* Video */}
                {(activeModule.content_type === 'video' || (!activeModule.content_type && activeModule.video_url)) && activeModule.video_url && (
                  <div className="mb-4 aspect-video rounded-xl overflow-hidden bg-black">
                    <iframe src={activeModule.video_url} title={activeModule.title} className="w-full h-full" allowFullScreen></iframe>
                  </div>
                )}
                {/* Image */}
                {activeModule.content_type === 'image' && activeModule.image_url && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-slate-950 border border-white/10">
                    <img src={activeModule.image_url} alt={activeModule.title} className="w-full h-auto object-contain max-h-[600px]" />
                  </div>
                )}
                {/* External URL — inline (same tab) */}
                {activeModule.content_type === 'url' && activeModule.external_url && (
                  <a href={activeModule.external_url} rel="noreferrer"
                     className="mb-4 flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold hover:scale-[1.01] transition"
                     data-testid="academy-external-link">
                    🔗 {activeModule.link_label || 'Open Link'}
                  </a>
                )}
                {/* Web Link — new tab */}
                {activeModule.content_type === 'web_link' && activeModule.external_url && (
                  <a href={activeModule.external_url} target="_blank" rel="noopener noreferrer"
                     className="mb-4 flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold hover:scale-[1.01] transition"
                     data-testid="academy-web-link">
                    🌐 {activeModule.link_label || 'Open Link'} <span className="text-xs opacity-70">↗</span>
                  </a>
                )}
                {activeModule.text_content && (
                  <div className="prose prose-invert max-w-none text-slate-200/90 whitespace-pre-wrap text-sm leading-relaxed">{activeModule.text_content}</div>
                )}
                {!activeModule.is_demo && (
                  <button onClick={() => completeModule(activeModule.id)} data-testid="academy-mark-complete" className="mt-5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Mark Complete (+{activeModule.points_reward} AT)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Academy;

// ============ Certificate Button (backend PDF with QR verify + optional quiz gate) ============
const CertificateButton = ({ courseId }) => {
  const [busy, setBusy] = useState(false);
  const [quizStatus, setQuizStatus] = useState({ quiz_exists: false, passed: false });
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    api.get(`/academy/course/${courseId}/quiz/status`).then((r) => setQuizStatus(r.data)).catch(() => {});
  }, [courseId]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/academy/certificate/${courseId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const cid = (resp.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `Anant_Certificate_${courseId}.pdf`;
      a.href = url; a.download = cid;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded — share on WhatsApp!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Certificate not yet available.');
    } finally { setBusy(false); }
  };

  // Quiz gate: if a quiz exists and user hasn't passed, force quiz first
  const handleClick = () => {
    if (quizStatus.quiz_exists && !quizStatus.passed) {
      setShowQuiz(true);
    } else {
      downloadPdf();
    }
  };

  const shareOnWhatsApp = async () => {
    try {
      const r = await api.get(`/academy/certificate/${courseId}/share-payload`);
      window.open(r.data.whatsapp, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Certificate share unavailable — complete a module first.');
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
      <button onClick={handleClick} disabled={busy} data-testid="academy-cert-btn"
        className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm disabled:opacity-60 flex items-center gap-2 shadow-lg">
        <Award className="w-4 h-4" />
        {busy ? 'Generating…' :
          quizStatus.quiz_exists && !quizStatus.passed ? 'Attempt Quiz to Unlock Certificate' :
          'Download Certificate PDF'}
      </button>
      {(!quizStatus.quiz_exists || quizStatus.passed) && (
        <button onClick={shareOnWhatsApp} data-testid="academy-cert-share-btn"
          className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-200 border border-emerald-400/40 font-bold text-xs flex items-center gap-1.5 transition">
          📤 Share on WhatsApp
        </button>
      )}
      </div>
      {showQuiz && (
        <CourseQuizModal
          courseId={courseId}
          onClose={() => setShowQuiz(false)}
          onPassed={() => { setShowQuiz(false); setQuizStatus((s) => ({ ...s, passed: true })); toast.success('Quiz passed! Now downloading your certificate…'); downloadPdf(); }}
        />
      )}
    </>
  );
};

// ============ Quiz Modal ============
const CourseQuizModal = ({ courseId, onClose, onPassed }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/academy/course/${courseId}/quiz`).then((r) => setQuiz(r.data)).catch(() => {});
  }, [courseId]);

  const submit = async () => {
    if (!quiz) return;
    const arr = quiz.questions.map((_, i) => (answers[i] == null ? -1 : answers[i]));
    setBusy(true);
    try {
      const r = await api.post(`/academy/course/${courseId}/quiz/submit`, { answers: arr });
      setResult(r.data);
      if (r.data.passed) setTimeout(() => onPassed && onPassed(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="course-quiz-modal">
      <div className="max-w-2xl w-full max-h-[90vh] rounded-2xl bg-slate-900 border-2 border-amber-400/40 flex flex-col">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="text-amber-300 font-bold">Course Quiz · Pass {quiz?.passing_score || 60}% to unlock certificate</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!quiz && <div className="text-slate-400">Loading quiz…</div>}
          {quiz && !result && quiz.questions.map((q, i) => (
            <div key={i} className="rounded-lg bg-slate-800/60 border border-slate-700 p-3" data-testid={`quiz-q-${i}`}>
              <div className="text-white text-sm font-bold mb-2">Q{i + 1}. {q.question}</div>
              <div className="space-y-1.5">
                {q.options.map((opt, j) => (
                  <label key={j} className={`flex items-start gap-2 p-2 rounded cursor-pointer text-sm ${answers[i] === j ? 'bg-amber-500/20 text-amber-100 border border-amber-400/40' : 'text-slate-300 hover:bg-slate-700/50'}`}>
                    <input type="radio" name={`q-${i}`} checked={answers[i] === j} onChange={() => setAnswers({ ...answers, [i]: j })}
                      data-testid={`quiz-q-${i}-opt-${j}`} className="mt-0.5" />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {result && (
            <div className={`p-4 rounded-lg text-center ${result.passed ? 'bg-emerald-500/20 border border-emerald-400' : 'bg-rose-500/20 border border-rose-400'}`}>
              <div className="text-3xl mb-2">{result.passed ? '🎉' : '😔'}</div>
              <div className="text-white text-xl font-bold">Score: {result.score_pct}%</div>
              <div className="text-slate-200 text-sm mt-1">{result.passed ? 'Passed — certificate unlocking!' : `Need ${result.passing_score}% to pass. Try again.`}</div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-700 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-slate-700 text-white text-sm">Close</button>
          {!result && (
            <button onClick={submit} disabled={busy || !quiz} data-testid="quiz-submit-btn"
              className="flex-1 py-2 rounded bg-amber-500 text-black font-bold text-sm disabled:opacity-50">
              {busy ? 'Submitting…' : 'Submit Quiz'}
            </button>
          )}
          {result && !result.passed && (
            <button onClick={() => { setResult(null); setAnswers({}); }}
              className="flex-1 py-2 rounded bg-amber-500 text-black font-bold text-sm">Retry</button>
          )}
        </div>
      </div>
    </div>
  );
};
